"use client";
import React, { useState, useEffect } from "react";
// Drawer modal removed - using fixed positioning drawer instead
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { SearchInput } from "../../../components/ui/search-input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { SearchableSelect } from "../../../components/ui/searchable-select";
import { cn } from "../../../lib/utils";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";
import { useSelector } from "react-redux";
import {
  calculateClothAreaPerUnit,
} from "./orderCalculations";
import { normalizeSearchQuery } from "../../../utils/search";
import { toMediaSrc, getColorImage } from "../../../utils/mediaSrc";
import { AuthMediaImg } from "../../../components/AuthMediaImg";
import { useOverlayHistory } from "../../../hooks/useOverlayHistory";
import { specArrayHasDuplicateTitles } from "./quotationSpecHelpers";

/** Unique form field per catalog spec row (avoids duplicate titles sharing one value). */
const specFormFieldKey = (specIndex: number) => `__spec_${specIndex}`;

// Utility function for smart decimal formatting
const formatSmartDecimal = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === "") return "";
  const raw = String(value).trim();
  // Keep in-progress decimals like "12." or "0."
  if (/^-?\d+\.$/.test(raw) || raw === "." || raw === "-.") return raw;
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return raw;

  if (num % 1 === 0) {
    return Math.round(num).toString();
  }
  return parseFloat(num.toFixed(4)).toString();
};

const parseLooseNumber = (value: any): number => {
  if (value === undefined || value === null || value === "") return 0;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Initial UOMs by product type (for display + calculation):
 * - length            → length Feet, area ft
 * - width_height      → W/H Feet, area Sq.ft
 * - width_height+cloth→ W/H m, area Mtr. (sq.m)
 * - pieces            → area Pcs.
 */
const getDefaultUoms = (
  formulaType?: string | null,
  isCloth?: boolean
): { wh_uom: string; length_uom: string; area_uom: string } => {
  const formula = String(formulaType || "width_height").trim().toLowerCase();
  if (formula === "pieces") {
    return { wh_uom: "Feet", length_uom: "Feet", area_uom: "Pcs." };
  }
  if (formula === "length") {
    return { wh_uom: "Feet", length_uom: "Feet", area_uom: "ft" };
  }
  // width_height
  if (isCloth) {
    return { wh_uom: "m", length_uom: "Feet", area_uom: "Mtr." };
  }
  return { wh_uom: "Feet", length_uom: "Feet", area_uom: "Sq.ft" };
};

const applyDefaultUomsToForm = (
  form: Partial<ProductItem>,
  formulaType?: string | null,
  isCloth?: boolean
) => {
  const defaults = getDefaultUoms(formulaType, isCloth);
  form.wh_uom = defaults.wh_uom;
  form.length_uom = defaults.length_uom;
  form.area_uom = defaults.area_uom;
};

const roundTo2 = (n: number): number => {
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

/** Display area values with exactly 2 digits after the decimal, but keep in-progress input like "12." */
const formatAreaDecimal = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === "") return "";
  const raw = String(value).trim();
  if (/^-?\d+\.$/.test(raw) || raw === "." || raw === "-.") return raw;
  // Keep the user's typed string (already capped at 2 decimals) so the cursor does not jump
  if (typeof value === "string" && /^-?\d+(\.\d{0,2})?$/.test(raw)) return raw;
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return raw;
  return num.toFixed(2);
};

/** Keep digits and a single decimal point while typing (e.g. "52." / "52.5"). */
const sanitizeDecimalInput = (raw: string, maxDecimals?: number): string => {
  const s = String(raw ?? "").replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot === -1) return s;
  const decimals = s.slice(firstDot + 1).replace(/\./g, "");
  const limited =
    typeof maxDecimals === "number" ? decimals.slice(0, maxDecimals) : decimals;
  return s.slice(0, firstDot + 1) + limited;
};

const findColorByName = (list: any[], name: string) => {
  const target = String(name || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!target) return undefined;
  return (list || []).find((c) => {
    const n = String(c?.name || c?.Name || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    return n === target || String(c?.id) === String(name);
  });
};

const colorOptionName = (item: any): string => {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  return String(item.name || item.value || item.title || item.Name || "");
};

const colorOptionImage = (item: any, colorsList: any[]): string => {
  if (item && typeof item === "object") {
    const embedded = toMediaSrc(
      item.image || item.colorImage || item.color_image || item.ColorImage
    );
    if (embedded) return embedded;
  }
  const color = findColorByName(colorsList, colorOptionName(item));
  return getColorImage(color);
};

const specValueList = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
};

const specRowColsClass = (count: number) => {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-1 md:grid-cols-2";
  return "grid-cols-1 md:grid-cols-3";
};

interface Product {
  id: string;
  name: string;
  gst: number;
  price: number;
  isCloth?: boolean;
  productImage?: string;
  hasImage?: boolean;
  specifications?: { title: string; type: string; value?: string[]; color?: string }[];
  formulaType?: string;
  categoryId?: number;
  categoryName?: string;
}

const productHasPlaceSpec = (product?: Product | null) =>
  Boolean(
    product?.specifications?.some(
      (s: any) => s?.type === "place" || String(s?.title || "").trim().toLowerCase() === "place"
    )
  );

interface ProductItem {
  product_id: string;
  name: string;
  width: number;
  wh_uom: string;
  nool_width: number;
  height: number;
  nool_height: number;
  nos: number;
  place: string;
  area: number;
  area_uom: string;
  area_per_unit: number;
  gst: number;
  price: number; // Amount (line total)
  price_per_unit?: number; // Rate / Price per unit
  isCloth?: boolean;
  customProductBasePrice?: number; // Base price for custom products (before multiplying by quantity)
  [key: string]: any;
}

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (products: ProductItem[]) => void;
  userRole: "admin" | "dealer" | "employee";
  customerRole:string;
  existingProduct?: ProductItem | null;
  otherModalOpen?: boolean;
}

const parseProductsResponse = (data: any): Product[] => {
  const list = data?.products || data?.Products || data?.data?.products || [];
  if (!Array.isArray(list)) return [];
  return list.map((p: any) => ({
    ...p,
    id: p.id ?? p.Id,
    name: p.name ?? p.Name,
    productImage: p.productImage ?? p.product_image ?? p.ProductImage ?? "",
    hasImage: Boolean(
      p.hasImage ??
        p.HasImage ??
        (p.productImage || p.product_image || p.ProductImage)
    ),
  }));
};

const getProductsTotalCount = (data: any, fallbackLen: number) => {
  const total = data?.totalCount ?? data?.TotalCount ?? data?.total_count;
  const n = typeof total === "number" ? total : parseInt(String(total || ""), 10);
  return Number.isFinite(n) ? n : fallbackLen;
};

const PICKER_PAGE_SIZE = 40;

const matchesProductSearch = (product: Product, term: string): boolean => {
  const q = normalizeSearchQuery(term).toLowerCase();
  if (!q) return true;
  const name = String(product.name || "").toLowerCase();
  const hsn = String((product as any).hsnCode ?? (product as any).hsn_code ?? "").toLowerCase();
  return name.includes(q) || hsn.includes(q);
};

export const ProductForm: React.FC<ProductFormProps> = ({
  open,
  onClose,
  onAdd,
  userRole,
  customerRole,
   existingProduct,
   otherModalOpen = false,
}) => {
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [productSearchInput, setProductSearchInput] = useState("");
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [pickerCategoryId, setPickerCategoryId] = useState<string>("all");
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [pickerProducts, setPickerProducts] = useState<Product[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerLoadingMore, setPickerLoadingMore] = useState(false);
  const [pickerHasMore, setPickerHasMore] = useState(true);
  const [pickerTotalCount, setPickerTotalCount] = useState(0);
  const [pickerOffset, setPickerOffset] = useState(0);
  const [sections, setSections] = useState<
    { selectedProduct: Product | null; form: Partial<ProductItem> }[]
  >([
    {
      selectedProduct: null,
      form: {
        ...getDefaultUoms("width_height", false),
        place: "",
        gst: 0,
        price: 0,
        price_per_unit: 0,
        customProductBasePrice: undefined,
      },
    },
  ]);

  const { authToken = "", id = "",role="" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const [colors, setColors] = useState<any[]>([]);
  const [sectionErrors, setSectionErrors] = useState<{ [key: number]: { [key: string]: string } }>({});
  const [openPopover, setOpenPopover] = useState<number | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [manualPriceEdited, setManualPriceEdited] = useState<{ [key: number]: boolean }>({});
  const [manualAreaPerUnitEdited, setManualAreaPerUnitEdited] = useState<{ [key: number]: boolean }>({});
  const [manualAreaEdited, setManualAreaEdited] = useState<{ [key: number]: boolean }>({});
  const [previewColor, setPreviewColor] = useState<any>(null);
  const [previewProduct, setPreviewProduct] = useState<any>(null);
  const prevSectionCount = React.useRef(1);
  const productsListRef = React.useRef(productsList);
  productsListRef.current = productsList;
  const pickerFetchGen = React.useRef(0);
  const pickerScrollRef = React.useRef<HTMLDivElement>(null);

  useOverlayHistory(open, onClose, "product-form-drawer");
  useOverlayHistory(openPopover !== null, () => {
    setOpenPopover(null);
    setProductSearchInput("");
    setProductSearchQuery("");
    setPickerCategoryId("all");
    setPickerProducts([]);
  }, "product-picker");
  useOverlayHistory(!!previewColor, () => setPreviewColor(null), "product-color-preview");
  useOverlayHistory(!!previewProduct, () => setPreviewProduct(null), "product-image-preview");


  // Hydrate only when the product being edited changes — never when productsList
  // refreshes (that was wiping serial/spec fields mid-edit).
  useEffect(() => {
    if (existingProduct) {
      const fullProduct =
        productsListRef.current.find((p) => p.id === existingProduct.product_id) || null;

      const formData: Partial<ProductItem> = { ...existingProduct };

      const catalogSpecs = fullProduct?.specifications || [];
      if (Array.isArray(existingProduct.specifications)) {
        existingProduct.specifications.forEach((entry: any, specIndex: number) => {
          const val = entry?.value?.name ?? entry?.value ?? entry?.name;
          if (val !== undefined && val !== null && val !== "") {
            (formData as Record<string, unknown>)[specFormFieldKey(specIndex)] = val;
          }
        });
      } else if (existingProduct.specifications && catalogSpecs.length) {
        catalogSpecs.forEach((spec: { title: string }, specIndex: number) => {
          const specValue = existingProduct.specifications?.[spec.title];
          if (specValue === undefined || specValue === null) return;
          const value =
            typeof specValue === "object" && specValue !== null && "name" in specValue
              ? (specValue as { name?: string }).name
              : specValue;
          (formData as Record<string, unknown>)[specFormFieldKey(specIndex)] = value;
        });
      } else if (existingProduct.specifications) {
        Object.keys(existingProduct.specifications).forEach((specTitle) => {
          const specValue = existingProduct.specifications![specTitle];
          const value = specValue?.name || specValue;
          (formData as Record<string, unknown>)[specTitle.toLowerCase()] = value;
        });
      }

      setSections([
        {
          selectedProduct: fullProduct || {
            id: existingProduct.product_id,
            name: existingProduct.name,
            gst: existingProduct.gst,
            price: existingProduct.price_per_unit ?? existingProduct.price,
            isCloth: existingProduct.isCloth,
            formulaType: existingProduct.formulaType,
          },
          form: {
            ...formData,
            price_per_unit:
              formData.price_per_unit ??
              (fullProduct ? parseFloat(fullProduct.price?.toString() || "0") : undefined),
          },
        },
      ]);
      setManualAreaEdited({ 0: false });
      setManualAreaPerUnitEdited({ 0: false });
    } else {
      setSections([
        {
          selectedProduct: null,
          form: {
            ...getDefaultUoms("width_height", false),
            place: "",
            gst: 0,
            price: 0,
          },
        },
      ]);
      setManualAreaEdited({ 0: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- productsList must not reset form
  }, [existingProduct]);

  // When catalog loads, attach full product (specs) without clearing user-entered form values
  useEffect(() => {
    if (!productsList.length) return;
    setSections((prev) => {
      let changed = false;
      const next = prev.map((section) => {
        const id = section.selectedProduct?.id;
        if (!id) return section;
        const full = productsList.find((p) => p.id === id);
        if (!full) return section;
        const hasSpecs =
          Array.isArray(section.selectedProduct?.specifications) &&
          (section.selectedProduct?.specifications?.length || 0) > 0;
        if (hasSpecs && section.selectedProduct?.formulaType) return section;
        changed = true;
        return {
          ...section,
          selectedProduct: {
            ...section.selectedProduct,
            ...full,
            // Prefer catalog specs when section lacked them
            specifications: full.specifications || section.selectedProduct?.specifications,
          },
        };
      });
      return changed ? next : prev;
    });
  }, [productsList]);

  useEffect(() => {
    const parseProducts = parseProductsResponse;
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}account/${id}/products`, {
          params: { firstResults: 0, maxResults: "all", lite: true },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const list = parseProducts(res.data);
        if (list.length) {
          setProductsList(list);
          return;
        }
      } catch (err) {
        console.error("Error fetching products:", err);
      }
      try {
        const fallback = await axios.get(`${API_BASE_URL}account/${id}/products`, {
          params: { firstResults: 0, maxResults: 1000, lite: true },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setProductsList(parseProducts(fallback.data));
      } catch (fallbackErr) {
        console.error("Error fetching products fallback:", fallbackErr);
        setProductsList([]);
      }
    };
    if (id && authToken) {
      fetchProducts();
    }
  }, [id, authToken]);

  useEffect(() => {
    const fetchColors = async () => {
      if (!id || !authToken) return;
      try {
        const { fetchColorsCached } = await import(
          "../../../utils/colorsCache"
        );
        // Lite list is fast; thumbnails load via /product/:id/image and /color/:id/image
        const { colors: colorsList } = await fetchColorsCached({
          accountId: id,
          authToken,
          lite: true,
          maxResults: "all",
          force: true,
        });
        setColors(Array.isArray(colorsList) ? colorsList : []);
      } catch (err) {
        console.error("Error fetching colors:", err);
        setColors([]);
      }
    };
    fetchColors();
  }, [id, authToken]);

  useEffect(() => {
    const fetchCategoriesList = async () => {
      if (!id || !authToken) return;
      try {
        const res = await axios.get(`${API_BASE_URL}account/${id}/categories`, {
          params: { firstResults: 0, maxResults: "all" },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategories([]);
      }
    };
    fetchCategoriesList();
  }, [id, authToken]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProductSearchQuery(normalizeSearchQuery(productSearchInput));
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchInput]);

  const fetchPickerPage = React.useCallback(
    async (offset: number, append: boolean) => {
      if (!id || !authToken || openPopover === null) return;
      const gen = ++pickerFetchGen.current;
      if (append) setPickerLoadingMore(true);
      else setPickerLoading(true);
      try {
        const params: Record<string, string | number | boolean> = {
          firstResults: offset,
          maxResults: PICKER_PAGE_SIZE,
          // Lite keeps list fast; AuthMediaImg loads each thumbnail separately
          lite: true,
        };
        if (productSearchQuery) params.search = productSearchQuery;
        if (pickerCategoryId && pickerCategoryId !== "all") {
          params.categoryId = Number(pickerCategoryId);
        }
        const res = await axios.get(`${API_BASE_URL}account/${id}/products`, {
          params,
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (gen !== pickerFetchGen.current) return;
        const page = parseProductsResponse(res.data);
        const total = getProductsTotalCount(res.data, offset + page.length);
        setPickerProducts((prev) => (append ? [...prev, ...page] : page));
        setPickerOffset(offset + page.length);
        setPickerTotalCount(total);
        setPickerHasMore(offset + page.length < total && page.length > 0);
      } catch (err) {
        console.error("Error fetching picker products:", err);
        if (gen !== pickerFetchGen.current) return;
        if (!append) {
          let fallback = productsListRef.current;
          if (pickerCategoryId && pickerCategoryId !== "all") {
            const catNum = Number(pickerCategoryId);
            fallback = fallback.filter(
              (p) => Number((p as any).categoryId) === catNum
            );
          }
          if (productSearchQuery) {
            fallback = fallback.filter((p) =>
              matchesProductSearch(p, productSearchQuery)
            );
          }
          setPickerProducts(fallback);
          setPickerHasMore(false);
          setPickerTotalCount(fallback.length);
        }
      } finally {
        if (gen === pickerFetchGen.current) {
          setPickerLoading(false);
          setPickerLoadingMore(false);
        }
      }
    },
    [id, authToken, openPopover, productSearchQuery, pickerCategoryId]
  );

  useEffect(() => {
    if (openPopover === null || !id || !authToken) return;
    setPickerProducts([]);
    setPickerOffset(0);
    setPickerHasMore(true);
    setPickerTotalCount(0);
    fetchPickerPage(0, false);
  }, [openPopover, productSearchQuery, pickerCategoryId, id, authToken, fetchPickerPage]);

  const handlePickerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!pickerHasMore || pickerLoading || pickerLoadingMore) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom) {
      fetchPickerPage(pickerOffset, true);
    }
  };

  useEffect(() => {
    if (sections.length > prevSectionCount.current) {
      const targetIndex = sections.length - 1;
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current?.querySelector(
          `[data-section="${targetIndex}"]`
        ) as HTMLElement | null;
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    prevSectionCount.current = sections.length;
  }, [sections.length]);

const handleChange = (
  index: number,
  key: keyof ProductItem,
  value: any,
  isFinalPriceEdit?: boolean
) => {
  // Track area/unit manual edits outside setState for consistency within this call
  let areaPerUnitManual = !!manualAreaPerUnitEdited[index];
  let areaManual = !!manualAreaEdited[index];
  let amountManual = !!manualPriceEdited[index];

  if (key === "area_per_unit") {
    areaPerUnitManual = true;
    setManualAreaPerUnitEdited({ ...manualAreaPerUnitEdited, [index]: true });
  }
  if (key === "area") {
    areaManual = true;
    setManualAreaEdited({ ...manualAreaEdited, [index]: true });
  }
  // Dimension size or UOM change recalculates Area/Unit from width × height (or length).
  if (["width", "height", "length", "wh_uom", "length_uom"].includes(String(key))) {
    areaPerUnitManual = false;
    areaManual = false;
    setManualAreaPerUnitEdited({ ...manualAreaPerUnitEdited, [index]: false });
    setManualAreaEdited({ ...manualAreaEdited, [index]: false });
  }
  // Quantity change must multiply Area = Area/Unit × Qty — never lock total Area
  // and divide Area/Unit (that caused intermittent wrong totals).
  if (key === "nos") {
    areaManual = false;
    setManualAreaEdited({ ...manualAreaEdited, [index]: false });
    // Scale from the Area/Unit already on the form (local only; don't sticky-lock).
    areaPerUnitManual = true;
  }
  if (["width", "height", "length", "wh_uom", "length_uom", "area_per_unit", "nos", "price_per_unit", "area"].includes(String(key))) {
    amountManual = false;
    setManualPriceEdited({ ...manualPriceEdited, [index]: false });
  }
  if (key === "price" && isFinalPriceEdit) {
    amountManual = true;
    setManualPriceEdited({ ...manualPriceEdited, [index]: true });
  }

  setSections((prev) => {
    const updated = [...prev];
    const isCustomProduct = updated[index].form.custom_name !== undefined;

    if (key === "formulaType") {
      if (isCustomProduct && !updated[index].selectedProduct?.id) {
        updated[index].selectedProduct = {
          ...(updated[index].selectedProduct || {}),
          formulaType: value,
        } as Product;
      } else {
        updated[index].selectedProduct = {
          ...updated[index].selectedProduct,
          formulaType: value,
        } as Product;
      }
      const clothNow = !!(
        updated[index].selectedProduct?.isCloth ||
        (updated[index].selectedProduct as any)?.is_cloth_ ||
        updated[index].form?.isCloth
      );
      applyDefaultUomsToForm(updated[index].form, String(value), clothNow);
    } else if (key === "price") {
      const priceValue = value === "" ? 0 : parseFloat(value) || 0;
      if (isCustomProduct && !isFinalPriceEdit) {
        updated[index].form.customProductBasePrice = priceValue;
        updated[index].form.price_per_unit = priceValue;
        const nos = parseFloat(updated[index].form.nos?.toString() || "1");
        updated[index].form.price = priceValue * nos;
        return updated;
      }
      updated[index].form.price = priceValue;
      return updated;
    } else if (key === "price_per_unit") {
      const rate = value === "" ? 0 : parseFloat(value) || 0;
      updated[index].form.price_per_unit = rate;
      if (isCustomProduct) {
        updated[index].form.customProductBasePrice = rate;
      }
    } else {
      updated[index].form[key] = value;
      if (isCustomProduct && key === "nos" && updated[index].form.customProductBasePrice !== undefined) {
        const basePrice = updated[index].form.customProductBasePrice ?? 0;
        const nos = parseFloat(value?.toString() || "1");
        updated[index].form.price = basePrice * nos;
      }
    }

    const formulaType = updated[index].selectedProduct?.formulaType;
    const w = parseLooseNumber(updated[index].form.width);
    const h = parseLooseNumber(updated[index].form.height);
    const l = parseLooseNumber(updated[index].form.length);
    const n = parseLooseNumber(updated[index].form.nos);
    const luom = updated[index].form.length_uom;
    const rate =
      parseFloat(updated[index].form.price_per_unit?.toString() || "") ||
      parseFloat(updated[index].selectedProduct?.price?.toString() || "0") ||
      0;
    if (updated[index].form.price_per_unit === undefined || updated[index].form.price_per_unit === null) {
      updated[index].form.price_per_unit = rate;
    }

    const shouldCalculatePrice = !amountManual && !isCustomProduct;
    const isCloth = !!(
      updated[index].selectedProduct?.isCloth ||
      (updated[index].selectedProduct as any)?.is_cloth_ ||
      updated[index].form?.isCloth
    );
    const quantity = n || 1;
    const whUom =
      updated[index].form.wh_uom ||
      (isCloth ? "m" : "Feet");

    if (!formulaType || formulaType === "width_height") {
      // ——— Cloth meter: A=round(W_in/20) × B=((H_in+12)/39.5); width round ≤.3↓ ≥.4↑ ———
      if (isCloth) {
        let clothApu = calculateClothAreaPerUnit(w, h, whUom, whUom);
        let clothTotal = clothApu * quantity;

        if (areaPerUnitManual || key === "area_per_unit") {
          const manualApu = parseLooseNumber(updated[index].form.area_per_unit);
          clothApu = Number.isFinite(manualApu) ? manualApu : 0;
          clothTotal = clothApu * quantity;
          if (key !== "area_per_unit") {
            updated[index].form.area_per_unit = clothApu;
          }
          if (key !== "area") {
            updated[index].form.area = clothTotal;
          }
        } else if (areaManual || key === "area") {
          const manualArea = parseLooseNumber(updated[index].form.area);
          clothTotal = Number.isFinite(manualArea) ? manualArea : 0;
          clothApu = quantity ? clothTotal / quantity : clothTotal;
          if (key !== "area") {
            updated[index].form.area = clothTotal;
          }
          updated[index].form.area_per_unit = clothApu;
        } else {
          updated[index].form.area_per_unit = clothApu;
          updated[index].form.area = clothTotal;
        }

        if (shouldCalculatePrice) {
          updated[index].form.price = clothTotal * rate;
        }
      } else {
      // ——— Non-cloth: width×height in feet / area UOM ———
      let widthInFeet = w;
      let heightInFeet = h;
      switch (whUom) {
        case "MM":
          widthInFeet = w / 304.8;
          heightInFeet = h / 304.8;
          break;
        case "CM":
          widthInFeet = w / 30.48;
          heightInFeet = h / 30.48;
          break;
        case "Inch":
          widthInFeet = w / 12;
          heightInFeet = h / 12;
          break;
        case "m":
          widthInFeet = w * 3.28084;
          heightInFeet = h * 3.28084;
          break;
        case "Feet":
        default:
          widthInFeet = w;
          heightInFeet = h;
      }

      const areaUom = updated[index].form.area_uom;
      let areaPerUnitSqFt = widthInFeet * heightInFeet;
      let totalAreaSqFt = areaPerUnitSqFt * quantity;

      if (areaPerUnitManual || key === "area_per_unit") {
        const manualApu = parseLooseNumber(updated[index].form.area_per_unit);
        switch (areaUom) {
          case "Mtr.":
            areaPerUnitSqFt = manualApu * 10.7639;
            break;
          case "Panels":
          case "No's":
          case "Pcs.":
            areaPerUnitSqFt = manualApu; // treat as count measure
            break;
          default:
            areaPerUnitSqFt = manualApu;
        }
        totalAreaSqFt =
          areaUom === "Panels" || areaUom === "No's" || areaUom === "Pcs."
            ? manualApu * quantity
            : areaPerUnitSqFt * quantity;
        if (key !== "area_per_unit") {
          updated[index].form.area_per_unit = manualApu;
        }
        if (key !== "area") {
          updated[index].form.area =
            areaUom === "Mtr." ? (totalAreaSqFt / 10.7639) : totalAreaSqFt;
          if (areaUom === "Panels" || areaUom === "No's" || areaUom === "Pcs.") {
            updated[index].form.area = manualApu * quantity;
          }
        }
      } else if (areaManual || key === "area") {
        // Keep user-edited total area (including decimals); derive area_per_unit from qty
        const manualArea = parseLooseNumber(updated[index].form.area);
        if (key !== "area") {
          updated[index].form.area = manualArea;
        }
        updated[index].form.area_per_unit = quantity ? manualArea / quantity : manualArea;
        switch (areaUom) {
          case "Mtr.":
            totalAreaSqFt = manualArea * 10.7639;
            break;
          default:
            totalAreaSqFt = manualArea;
        }
      } else {
        switch (areaUom) {
          case "Sq.ft":
            updated[index].form.area_per_unit = areaPerUnitSqFt;
            updated[index].form.area = totalAreaSqFt;
            break;
          case "Mtr.": {
            const areaPerUnitSqM = areaPerUnitSqFt / 10.7639;
            updated[index].form.area_per_unit = areaPerUnitSqM;
            updated[index].form.area = areaPerUnitSqM * quantity;
            break;
          }
          case "Panels":
          case "No's":
          case "Pcs.":
            updated[index].form.area_per_unit = quantity;
            updated[index].form.area = quantity;
            totalAreaSqFt = quantity;
            break;
          default:
            updated[index].form.area_per_unit = areaPerUnitSqFt;
            updated[index].form.area = totalAreaSqFt;
        }
      }

      if (shouldCalculatePrice) {
        updated[index].form.price = totalAreaSqFt * rate;
      }
      } // end non-cloth
    } else if (formulaType === "length") {
      let lengthInFeet = l;
      switch (luom) {
        case "MM":
          lengthInFeet = l / 304.8;
          break;
        case "CM":
          lengthInFeet = l / 30.48;
          break;
        case "Inch":
          lengthInFeet = l / 12;
          break;
        case "Feet":
          lengthInFeet = l;
          break;
        case "m":
          lengthInFeet = l * 3.28084;
          break;
        default:
          lengthInFeet = l;
      }

      if (areaPerUnitManual || key === "area_per_unit") {
        lengthInFeet = parseLooseNumber(updated[index].form.area_per_unit);
      }

      const totalFeet = lengthInFeet * quantity;
      if (key !== "area_per_unit") {
        updated[index].form.area_per_unit = lengthInFeet;
      }
      if (areaManual || key === "area") {
        const manualArea = parseLooseNumber(updated[index].form.area);
        if (key !== "area") {
          updated[index].form.area = manualArea;
        }
        if (shouldCalculatePrice) {
          updated[index].form.price = manualArea * rate;
        }
      } else {
        updated[index].form.area = totalFeet;
        if (shouldCalculatePrice) {
          updated[index].form.price = totalFeet * rate;
        }
      }
    } else if (formulaType === "pieces") {
      // Piece-based: qty only — no area UI; price = rate × quantity
      updated[index].form.area_uom = "Pcs.";
      updated[index].form.area_per_unit = 1;
      updated[index].form.area = quantity;
      if (shouldCalculatePrice) {
        updated[index].form.price = quantity * rate;
      }
    }

    if (key !== "area_per_unit" && updated[index].form.area_per_unit != null) {
      updated[index].form.area_per_unit = roundTo2(parseLooseNumber(updated[index].form.area_per_unit));
    }
    if (key !== "area" && updated[index].form.area != null) {
      updated[index].form.area = roundTo2(parseLooseNumber(updated[index].form.area));
    }

    return updated;
  });
};


  const addSection = () => {
    setSections([
      ...sections,
      {
        selectedProduct: null,
        form: {
          width: 0,
          ...getDefaultUoms("width_height", false),
          nool_width: 0,
          height: 0,
          nool_height: 0,
          nos: 0,
          place: "",
          area: 0,
          area_per_unit: 0,
          gst: 0,
          price: 0,
          customProductBasePrice: undefined,
          custom_name: undefined,
        },
      },
    ]);
  };

  const duplicateSection = (index: number) => {
    const clone = JSON.parse(JSON.stringify(sections[index]));
    setSections([...sections.slice(0, index + 1), clone, ...sections.slice(index + 1)]);
  };

  const deleteSection = (index: number) => {
    if (sections.length === 1) return;
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const newSectionErrors: typeof sectionErrors = {};
    let hasErrors = false;

    // Validate each section
    sections.forEach((section, index) => {
      const errors: { [key: string]: string } = {};
      const { selectedProduct, form } = section;
     const formulaType = selectedProduct?.formulaType;
const isLengthFormula = formulaType === "length";
const isWidthHeightFormula =
  !formulaType || formulaType === "width_height";

// Validate dimensions
if (isWidthHeightFormula) {
  if (!form.width || form.width === 0) {
    errors.width = "Width is required";
    hasErrors = true;
  }
    if (!form.wh_uom) {
        errors.wh_uom = "Width/Height UOM is required";
        hasErrors = true;
      }

  if (!form.height || form.height === 0) {
    errors.height = "Height is required";
    hasErrors = true;
  }
    
}

if (isLengthFormula) {
  if (!form.length || form.length === 0) {
    errors.length = "Length is required";
    hasErrors = true;
  }
    if (!form.length_uom) {
        errors.length_uom = "Length UOM is required";
        hasErrors = true;
      }
}

      // Check required fields
      if (!selectedProduct && !form.custom_name) {
        errors.product = "Please select a product";
        hasErrors = true;
      }
     
      if (!form.nos || form.nos === 0) {
        errors.nos = "Quantity is required";
        hasErrors = true;
      }

      // Check specifications
      if (selectedProduct?.specifications && selectedProduct.specifications.length > 0) {
        selectedProduct.specifications.forEach((spec, specIndex) => {
          const specValue = (form as Record<string, unknown>)[specFormFieldKey(specIndex)];
          if (!specValue || specValue === "") {
            errors[`spec_${specIndex}`] = `${spec.title} is required`;
            hasErrors = true;
          }
        });
      }

      if (Object.keys(errors).length > 0) {
        newSectionErrors[index] = errors;
      }
    });

    setSectionErrors(newSectionErrors);

    // If there are errors, scroll to first error
    if (hasErrors) {
      setTimeout(() => {
        const firstErrorIndex = Math.min(...Object.keys(newSectionErrors).map(Number));
        const errorSection = scrollContainerRef.current?.querySelector(`[data-section="${firstErrorIndex}"]`);
        if (errorSection) {
          errorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return;
    }

    const products: ProductItem[] = sections.map((s) => {
      const { selectedProduct, form } = s;
      
      // Build specifications in catalog order (array when duplicate titles exist)
      const specifications: { [key: string]: any } = {};
      const specArray: Array<{ title: string; type?: string; value: unknown }> = [];
      const specOrder: string[] = [];
      const catalogSpecs = selectedProduct?.specifications || [];
      const useSpecArray = specArrayHasDuplicateTitles(catalogSpecs);

      catalogSpecs.forEach((spec, specIndex) => {
        const specValue = (form as Record<string, unknown>)[specFormFieldKey(specIndex)];
        if (specValue === undefined || specValue === null || specValue === "") return;

        let stored: unknown = specValue;
        if (spec.type === "color") {
          const selectedColor = findColorByName(colors, String(specValue));
          stored = {
            name: specValue,
            id: selectedColor?.id ?? null,
            image: getColorImage(selectedColor) || null,
          };
        }

        specOrder.push(spec.title);
        if (useSpecArray) {
          specArray.push({ title: spec.title, type: spec.type, value: stored });
        } else {
          specifications[spec.title] = stored;
        }
      });
      const formulaType = selectedProduct?.formulaType;
const isLengthFormula = formulaType === "length";
const isWidthHeightFormula =
  !formulaType || formulaType === "width_height";

return {
  product_id: selectedProduct?.id || "",
  name: form.custom_name || selectedProduct?.name || "",

  width: isWidthHeightFormula ? form.width || 0 : 0,
  wh_uom: isWidthHeightFormula ? form.wh_uom || (selectedProduct?.isCloth || (selectedProduct as any)?.is_cloth_ ? "m" : "Feet") : "",
  nool_width: isWidthHeightFormula ? form.nool_width || 0 : 0,

  height: isWidthHeightFormula ? form.height || 0 : 0,
  nool_height: isWidthHeightFormula ? form.nool_height || 0 : 0,

  length: isLengthFormula ? form.length || 0 : 0,
  length_uom: isLengthFormula ? form.length_uom || "Feet" : "",

  formulaType: selectedProduct?.formulaType || "",
  isCloth: !!(selectedProduct?.isCloth || (selectedProduct as any)?.is_cloth_),

  nos: form.nos || 1,
  place: (() => {
    const fromField = String(form.place ?? "").trim();
    if (fromField) return fromField;
    for (const [key, val] of Object.entries(specifications)) {
      if (String(key).trim().toLowerCase() !== "place") continue;
      if (val && typeof val === "object" && "name" in val) {
        return String((val as { name?: string }).name ?? "").trim();
      }
      return String(val ?? "").trim();
    }
    return "";
  })(),
  area: roundTo2(parseLooseNumber(form.area)),
  area_uom:
    formulaType === "pieces"
      ? "Pcs."
      : form.area_uom ||
        getDefaultUoms(formulaType, !!(selectedProduct?.isCloth || (selectedProduct as any)?.is_cloth_))
          .area_uom,
  area_per_unit: formulaType === "pieces" ? 1 : roundTo2(parseLooseNumber(form.area_per_unit)),
  gst: form.gst ?? selectedProduct?.gst ?? 0,
  price: form.price ?? 0,
  price_per_unit: form.price_per_unit ?? selectedProduct?.price ?? 0,
  specifications: useSpecArray ? (specArray as unknown as ProductItem["specifications"]) : specifications,
  specOrder: specOrder.length ? specOrder : undefined,
  catalogSpecTitles: selectedProduct?.specifications?.map((s) => s.title) ?? undefined,
};   
    });
    onAdd(products);
    // Reset sections after submitting
    setSections([
      {
        selectedProduct: null,
        form: {
          width: 0,
          ...getDefaultUoms("width_height", false),
          nool_width: 0,
          height: 0,
          nool_height: 0,
          length: 0,
          nos: 0,
          place: "",
          area: 0,
          area_per_unit: 0,
          gst: 0,
          price: 0,
          customProductBasePrice: undefined,
          custom_name: undefined,
        },
      },
    ]);
    setSectionErrors({});
    onClose();
  };

  const closeProductPicker = () => {
    setOpenPopover(null);
    setProductSearchInput("");
    setProductSearchQuery("");
    setPickerCategoryId("all");
    setPickerProducts([]);
  };

  const openProductPicker = (index: number) => {
    if (previewProduct || previewColor) return;
    setProductSearchInput("");
    setProductSearchQuery("");
    setPickerCategoryId("all");
    setPickerProducts(productsList);
    setOpenPopover(index);
  };

  const pickerSection = openPopover !== null ? sections[openPopover] : null;

  const filteredPickerProducts = React.useMemo(() => {
    if (openPopover === null) return [];
    return pickerProducts;
  }, [openPopover, pickerProducts]);

  const handleSelectProduct = (index: number, p: Product) => {
    const updated = [...sections];
    updated[index].selectedProduct = p;
    updated[index].form.gst = p.gst;
    const rate = parseFloat(p.price?.toString() || "0") || 0;
    updated[index].form.price_per_unit = rate;
    updated[index].form.custom_name = undefined;
    setManualPriceEdited({ ...manualPriceEdited, [index]: false });
    setManualAreaPerUnitEdited({ ...manualAreaPerUnitEdited, [index]: false });

    const isClothSel = !!(p.isCloth || (p as any).is_cloth_);
    applyDefaultUomsToForm(updated[index].form, p.formulaType, isClothSel);
    if (!productHasPlaceSpec(p)) {
      updated[index].form.place = "";
    }

    const fw = parseFloat(updated[index].form.width?.toString() || "0");
    const fh = parseFloat(updated[index].form.height?.toString() || "0");
    const fq = parseFloat(updated[index].form.nos?.toString() || "1") || 1;
    const fuom = updated[index].form.wh_uom || (isClothSel ? "m" : "Feet");
    if (p.formulaType === "pieces") {
      updated[index].form.area_uom = "Pcs.";
      updated[index].form.area_per_unit = 1;
      updated[index].form.area = fq;
      updated[index].form.price = fq * rate;
    } else if (isClothSel && fw > 0 && fh > 0) {
      const apu = roundTo2(calculateClothAreaPerUnit(fw, fh, fuom, fuom));
      updated[index].form.area_per_unit = apu;
      updated[index].form.area = roundTo2(apu * fq);
      updated[index].form.price = apu * fq * rate;
    } else if (
      (!p.formulaType || p.formulaType === "width_height") &&
      !isClothSel &&
      fw > 0 &&
      fh > 0
    ) {
      // Recalc with Feet / Sq.ft defaults
      let widthInFeet = fw;
      let heightInFeet = fh;
      if (fuom === "m") {
        widthInFeet = fw * 3.28084;
        heightInFeet = fh * 3.28084;
      } else if (fuom === "Inch") {
        widthInFeet = fw / 12;
        heightInFeet = fh / 12;
      } else if (fuom === "CM") {
        widthInFeet = fw / 30.48;
        heightInFeet = fh / 30.48;
      } else if (fuom === "MM") {
        widthInFeet = fw / 304.8;
        heightInFeet = fh / 304.8;
      }
      const areaPerUnit = roundTo2(widthInFeet * heightInFeet);
      const totalArea = roundTo2(areaPerUnit * fq);
      updated[index].form.area_per_unit = areaPerUnit;
      updated[index].form.area = totalArea;
      updated[index].form.price = totalArea * rate;
    } else {
      updated[index].form.price = 0;
    }

    setSections(updated);
    closeProductPicker();
  };

  const handleAddCustomProduct = (index: number) => {
    const updated = [...sections];
    updated[index].selectedProduct = null;
    updated[index].form.custom_name = "";
    updated[index].form.place = "";
    setSections(updated);
    closeProductPicker();
  };

  return (
    <>
      {/* Drawer Overlay */}
      {open && !otherModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[55]" onClick={onClose} />
      )}

      {/* Drawer: flex-centered so dropdowns are not clipped by CSS transform */}
      <div
        className={`fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 ${
          open ? "pointer-events-none opacity-100 visible" : "pointer-events-none invisible opacity-0"
        }`}
      >
      <div
        className={`pointer-events-auto bg-white w-full md:w-[65vw] max-h-[90vh] rounded-t-2xl md:rounded-lg flex flex-col shadow-2xl ${
          open ? "translate-y-0" : "translate-y-full md:translate-y-0"
        }`}
      >
        {/* Header */}
        <div className="bg-whitepx-6 py-5 px-5 border-b-2 border-emerald-800/30 sticky top-0 flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="font-bold text-2xl text-black drop-shadow-lg">
            {existingProduct ? "✏️ Edit Product" : "🛍️ Add Products"}
          </h2>
          <button
            onClick={onClose}
            className="text-black hover:text-green-600 text-2xl"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6 p-6 flex-1 min-h-0 overflow-y-auto overflow-x-hidden pointer-events-auto" ref={scrollContainerRef}>
          {sections.map((section, index) => (
            <div
              key={index}
              data-section={index}
      className="border-2 border-emerald-200/50 rounded-xl p-5 shadow-md bg-gradient-to-br from-white to-emerald-50 hover:shadow-lg transition-all min-h-[270px]"
            >
              {/* Error Alert for Section */}
              {sectionErrors[index] && (
                <div className="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded-xl flex gap-2 animate-shake">
                  <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  </div>
                  <div className="text-red-700 text-xs font-bold">
                    {Object.values(sectionErrors[index]).map((err, i) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                </div>
              )}
              {/* HEADER ROW */}
              <div className="mb-5">
                {/* First Row: Label and Buttons */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-3">
                  <Label className="font-bold text-gray-800">{index + 1}. Product {sectionErrors[index]?.product && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].product}</span>}</Label>
                  <div className="flex gap-2 w-full md:w-auto flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addSection}
                      className="border-2 border-green-400 text-green-700 hover:bg-green-50 font-bold flex-1 md:flex-auto rounded-lg h-10"
                    >
                      ➕ Add
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => duplicateSection(index)}
                      className="bg-cyan-500 text-white hover:bg-cyan-600 font-bold flex-1 md:flex-auto rounded-lg h-10"
                    >
                      📋 Duplicate
                    </Button>
                    {sections.length > 1 && (
                      <Button 
                        size="sm" 
                        onClick={() => deleteSection(index)}
                        className="bg-red-500 text-white hover:bg-red-600 font-bold flex-1 md:flex-auto rounded-lg h-10"
                      >
                        🗑️ Delete
                      </Button>
                    )}
                  </div>
                </div>

                {/* Product selector trigger */}
                <div
                  className={`${sectionErrors[index]?.product ? 'bg-red-50 p-2 rounded-lg border border-red-200' : ''}`}
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between border-2 border-emerald-300 hover:border-emerald-500 h-12 rounded-lg font-semibold text-gray-700"
                    onClick={() => openProductPicker(index)}
                  >
                    <span className="truncate">
                      {section.selectedProduct?.name ||
                        (section.form.custom_name !== undefined && section.form.custom_name !== ""
                          ? section.form.custom_name
                          : "Select Product")}
                    </span>
                    <span className="ml-2 shrink-0 text-gray-500">▾</span>
                  </Button>
                </div>
              </div>

              {section.form.custom_name !== undefined && (
                <>
                 <div className="mb-4 p-3 rounded-lg border-2 border-blue-300 bg-blue-50">
                  <Label className="font-bold text-gray-800 mb-2 block">Custom Product Name</Label>
                  <Input
                    placeholder="Enter custom product name"
                    value={section.form.custom_name || ""}
                    onChange={(e) => handleChange(index, "custom_name", e.target.value)}
                    className="border-2 border-blue-300 rounded-lg font-medium"
                  />
                </div>
                {/* Base Price Field for Custom Products */}
                {role != "dealer" && (
    <div className="mb-4 p-3 rounded-lg border-2 border-green-300 bg-green-50">
      <Label className="font-bold text-gray-800 mb-2 block">💵 Base Price * (per unit)</Label>
      <Input
        type="text"
        placeholder="Enter base price per unit"
        value={formatSmartDecimal(section.form.customProductBasePrice !== undefined ? section.form.customProductBasePrice : "")}
        onChange={(e) => handleChange(index, "price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0, false)}
        className="border-2 border-green-300 rounded-lg font-medium"
        required
      />
      <p className="text-xs text-gray-600 mt-2">Final price will be calculated as: Base Price × Quantity</p>
    </div>
                )}
                
                <div className="border-t pt-4">
                  <Label className="text-gray-700 font-semibold block mb-3">
                    Formula Choice *
                  </Label>
                
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formulaType"
                        value="width_height"
                        checked={section.selectedProduct?.formulaType === "width_height"}
                        onChange={(e) => handleChange(index, "formulaType", e.target.value)}
                        className="accent-blue-600"
                        required
                      />
                      <span className="text-sm text-gray-700">Width × Height</span>
                    </label>
                
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formulaType"
                        value="length"
                        checked={section.selectedProduct?.formulaType === "length"}
                        onChange={(e) => handleChange(index, "formulaType", e.target.value)}
                        className="accent-blue-600"
                      />
                      <span className="text-sm text-gray-700">Length</span>
                    </label>
                
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="formulaType"
                        value="pieces"
                        checked={section.selectedProduct?.formulaType === "pieces"}
                        onChange={(e) => handleChange(index, "formulaType", e.target.value)}
                        className="accent-blue-600"
                      />
                      <span className="text-sm text-gray-700">Pieces</span>
                    </label>
                  </div>
                </div>
                </>
               
              )}

              {/* PRODUCT SPECIFICATIONS — up to 3 per row */}
              {section.selectedProduct && (() => {
                const specList = section.selectedProduct.specifications || [];
                type SpecRowItem =
                  | { kind: "spec"; spec: (typeof specList)[number]; specIndex: number };
                const rowItems: SpecRowItem[] = specList.map((spec, specIndex) => ({
                  kind: "spec" as const,
                  spec,
                  specIndex,
                }));
                if (rowItems.length === 0) return null;
                const specRows: SpecRowItem[][] = [];
                for (let r = 0; r < rowItems.length; r += 3) {
                  specRows.push(rowItems.slice(r, r + 3));
                }

                const renderSpecField = (spec: (typeof specList)[number], specIndex: number) => {
                  const fieldKey = specFormFieldKey(specIndex);
                  const specErrorKey = `spec_${specIndex}`;
                  const colorOptions = specValueList(spec.value);
                  const hasError = Boolean(sectionErrors[index]?.[specErrorKey]);
                  return (
                    <div key={`spec-${specIndex}`} className="space-y-2">
                      <Label className="font-semibold text-gray-700 block">
                        {spec.title}{" "}
                        {hasError && (
                          <span className="text-red-600 text-xs font-normal ml-1">
                            {sectionErrors[index][specErrorKey]}
                          </span>
                        )}
                      </Label>
                      {spec.type === "color" ? (
                        <SearchableSelect
                          value={String((section.form as Record<string, unknown>)[fieldKey] ?? "")}
                          onChange={(val) => handleChange(index, fieldKey, val)}
                          placeholder="Select color..."
                          searchPlaceholder="Search color..."
                          triggerClassName={hasError ? "border-red-400" : "border-gray-300"}
                          showSelectedNode
                          options={colorOptions.map((colorItem: any, colorIdx: number) => {
                            const colorName = colorOptionName(colorItem);
                            const matched = findColorByName(colors, colorName);
                            const imageSrc = colorOptionImage(colorItem, colors);
                            return {
                              value: colorName,
                              label: colorName || `Color ${colorIdx + 1}`,
                              node: (
                                <AuthMediaImg
                                  kind="color"
                                  resourceId={matched?.id}
                                  accountId={id}
                                  authToken={authToken}
                                  inlineSrc={imageSrc}
                                  hasImage={Boolean(
                                    matched?.hasImage || matched?.colorImage || imageSrc
                                  )}
                                  alt={colorName}
                                  className="w-6 h-6 rounded border border-pink-300 object-cover shrink-0 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewColor({
                                      name: colorName,
                                      colorImage: imageSrc || undefined,
                                      id: matched?.id,
                                    });
                                  }}
                                  fallback={
                                    <div className="w-6 h-6 rounded border border-pink-300 bg-gray-200 shrink-0" />
                                  }
                                />
                              ),
                            };
                          })}
                        />
                      ) : (spec.type === "dropdown" || spec.type === "place") && spec.value ? (
                        <SearchableSelect
                          value={String((section.form as Record<string, unknown>)[fieldKey] ?? "")}
                          onChange={(val) => {
                            handleChange(index, fieldKey, val);
                            if (
                              spec.type === "place" ||
                              String(spec.title || "").trim().toLowerCase() === "place"
                            ) {
                              handleChange(index, "place", val);
                            }
                          }}
                          placeholder="Select..."
                          searchPlaceholder={`Search ${spec.title}...`}
                          triggerClassName={hasError ? "border-red-400" : "border-gray-300"}
                          options={specValueList(spec.value).map((v: any) => {
                            const option = colorOptionName(v) || String(v);
                            return { value: option, label: option };
                          })}
                        />
                      ) : (
                        <Input
                          type="text"
                          value={String((section.form as Record<string, unknown>)[fieldKey] ?? "")}
                          onChange={(e) => handleChange(index, fieldKey, e.target.value)}
                          className={`border-2 rounded-lg font-medium ${
                            hasError ? "border-red-400" : "border-gray-300"
                          }`}
                        />
                      )}
                    </div>
                  );
                };

                return (
                  <div className="mt-4 space-y-4">
                    {specRows.map((row, rowIdx) => (
                      <div
                        key={`spec-row-${index}-${rowIdx}`}
                        className={`grid gap-4 ${specRowColsClass(row.length)}`}
                      >
                        {row.map((item) => renderSpecField(item.spec, item.specIndex))}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Width / Height / Nos */}
              {(!section.selectedProduct?.formulaType ||
  section.selectedProduct?.formulaType === "width_height") &&  section && section.selectedProduct  &&  (
  <>
             <div
  className={`grid grid-cols-1 ${
    section.form.wh_uom === "Inch"
      ? "md:grid-cols-3"
      : "md:grid-cols-2"
  } gap-4 mt-4`}
>

                <div className={`bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.width ? 'border-red-400 bg-red-50' : 'border-blue-200'}`}>
                  <Label className="font-bold text-gray-700 mb-2 block">📏 Width {sectionErrors[index]?.width && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].width}</span>}</Label>
                  <Input
                    value={section.form.width || ""}
                    type="number"
                    step="0.01"
                    onChange={(e) =>
                      handleChange(index, "width", parseFloat(e.target.value) || 0)
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-blue-300 rounded-lg font-bold text-blue-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                  />
                </div>
                <div className={`bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.wh_uom ? 'border-red-400 bg-red-50' : 'border-purple-200'}`}>
                  <Label className="font-bold text-gray-700 mb-2 block">📐 UOM {sectionErrors[index]?.wh_uom && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].wh_uom}</span>}</Label>
                  <Select
                     value={section.form.wh_uom} 
                    onValueChange={(val) => handleChange(index, "wh_uom", val)}
                    defaultValue="m"
                  >
                    <SelectTrigger className="border-2 border-purple-300 rounded-lg font-bold">
                      <span>{section.form.wh_uom}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM">MM</SelectItem>
                      <SelectItem value="CM">CM</SelectItem>
                      <SelectItem value="Inch">Inch</SelectItem>
                      <SelectItem value="Feet">Feet</SelectItem>
                      <SelectItem value="m">Mtrs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
               {section.form.wh_uom === "Inch" && <div className="bg-white p-3 rounded-lg border-2 border-orange-200">
                  <Label className="font-bold text-gray-700 mb-2 block">🧵 Nool</Label>
                  <Input
                  value={section.form.nool_width || ""}
                    type="text"
                    onChange={(e) =>
                      handleChange(index, "nool_width", e.target.value)
                    }
                    className="border-2 border-orange-300 rounded-lg font-bold text-orange-600"
                  />
                </div>}
              </div>

              <div
  className={`grid grid-cols-1 ${
    section.form.wh_uom === "Inch"
      ? "md:grid-cols-3"
      : "md:grid-cols-2"
  } gap-4 mt-4`}
>

                <div className={`bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.height ? 'border-red-400 bg-red-50' : 'border-cyan-200'}`}>
                  <Label className="font-bold text-gray-700 mb-2 block">📏 Height {sectionErrors[index]?.height && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].height}</span>}</Label>
                  <Input
                   value={section.form.height || ""}
                    type="number"
                    step="0.01"
                    onChange={(e) =>
                      handleChange(index, "height", parseFloat(e.target.value) || 0)
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-cyan-300 rounded-lg font-bold text-cyan-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                  />
                </div>
                <div className={`bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.wh_uom ? 'border-red-400 bg-red-50' : 'border-green-200'}`}>
                  <Label className="font-bold text-gray-700 mb-2 block">📐 UOM {sectionErrors[index]?.wh_uom && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].wh_uom}</span>}</Label>
                  <Select
                  value={section.form.wh_uom}
                    onValueChange={(val) => handleChange(index, "wh_uom", val)}
                    defaultValue="m"
                  >
                    <SelectTrigger className="border-2 border-green-300 rounded-lg font-bold">
                      <span>{section.form.wh_uom}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM">MM</SelectItem>
                      <SelectItem value="CM">CM</SelectItem>
                      <SelectItem value="Inch">Inch</SelectItem>
                      <SelectItem value="Feet">Feet</SelectItem>
                      <SelectItem value="m">Mtrs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
               { section.form.wh_uom === "Inch" &&<div className="bg-white p-3 rounded-lg border-2 border-red-200">
                  <Label className="font-bold text-gray-700 mb-2 block">🧵 Nool</Label>
                  <Input
                  value={section.form.nool_height || ""}
                    type="text"
                    onChange={(e) =>
                      handleChange(index, "nool_height", e.target.value)
                    }
                    className="border-2 border-red-300 rounded-lg font-bold text-red-600"
                  />
                </div>}
              </div>
              </>)}
              {section.selectedProduct?.formulaType === "length" && section && section.selectedProduct  &&  (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className={`bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.length ? 'border-red-400 bg-red-50' : 'border-cyan-200'}`}>
                  <Label className="font-bold text-gray-700 mb-2 block">📏 Length {sectionErrors[index]?.length && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].length}</span>}</Label>
                  <Input
                   value={section.form.length || ""}
                    type="number"
                    step="0.01"
                    onChange={(e) =>
                      handleChange(index, "length", parseFloat(e.target.value) || 0)
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-cyan-300 rounded-lg font-bold text-cyan-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                  />
                </div>
                <div className={`bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.length_uom ? 'border-red-400 bg-red-50' : 'border-green-200'}`}>
                  <Label className="font-bold text-gray-700 mb-2 block">📐 UOM {sectionErrors[index]?.length_uom && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].length_uom}</span>}</Label>
                  <Select
                  value={section.form.length_uom}
                    onValueChange={(val) => handleChange(index, "length_uom", val)}
                    defaultValue="m"
                  >
                    <SelectTrigger className="border-2 border-green-300 rounded-lg font-bold">
                      <span>{section.form.length_uom}</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MM">MM</SelectItem>
                      <SelectItem value="CM">CM</SelectItem>
                      <SelectItem value="Inch">Inch</SelectItem>
                      <SelectItem value="Feet">Feet</SelectItem>
                      <SelectItem value="m">Mtrs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              )}
            {role != "dealer" && section && section.selectedProduct && section.selectedProduct?.formulaType !== "pieces" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white p-3 rounded-lg border-2 border-violet-200">
                  <Label className="font-bold text-gray-700 mb-2 block">📊 Area/Unit</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formatAreaDecimal(section.form.area_per_unit)}
                    step="0.01"
                    onChange={(e) =>
                      handleChange(index, "area_per_unit", sanitizeDecimalInput(e.target.value, 2))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-violet-300 rounded-lg font-bold text-violet-600"
                  />
                </div>
                <div className="bg-white p-3 rounded-lg border-2 border-fuchsia-200">
                  <Label className="font-bold text-gray-700 mb-2 block">📐 UOM</Label>
                  <Select
                    value={section.form.area_uom}
                    onValueChange={(val) => handleChange(index, "area_uom", val)}
                    defaultValue="ft"
                  >
                    <SelectTrigger className="border-2 border-fuchsia-300 rounded-lg font-bold">
                      <span>{section.form.area_uom}</span>
                    </SelectTrigger>
                    <SelectContent>
                        { section.selectedProduct?.formulaType !="length" && <SelectItem value="Sq.ft">Sq.ft</SelectItem>} 
                      <SelectItem value="ft">Feet</SelectItem>
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="Mtr.">Mtr.</SelectItem>}
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="Panels">Panels</SelectItem>}
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="No's">No's</SelectItem>}
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="Pcs.">Pcs.</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

              { section && section.selectedProduct  && <div className={`mt-4 bg-white p-3 rounded-lg border-2 ${sectionErrors[index]?.nos ? 'border-red-400 bg-red-50' : 'border-indigo-200'}`}>
                <Label className="font-bold text-gray-700 mb-2 block">📦 Quantity {sectionErrors[index]?.nos && <span className="text-red-600 text-xs ml-1">{sectionErrors[index].nos}</span>}</Label>
                <Input
                 value={section.form.nos || ""}
                  type="number"
                  onChange={(e) =>
                    handleChange(index, "nos", parseFloat(e.target.value) || 0)
                  }
                  onWheel={(e) => e.currentTarget.blur()}
                  className="border-2 border-indigo-300 rounded-lg font-bold text-indigo-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                />
              </div>}

              {/* Area / Price / GST — hide Area for piece-based products */}
              { section && section.selectedProduct && section.selectedProduct?.formulaType !== "pieces" && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {role != "dealer" && <div className="bg-white p-3 rounded-lg border-2 border-yellow-200">
                  <Label className="font-bold text-gray-700 mb-2 block">📊 Area</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formatAreaDecimal(section.form.area)}
                    onChange={(e) => handleChange(index, "area", sanitizeDecimalInput(e.target.value, 2))}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-yellow-300 rounded-lg font-bold text-yellow-600"
                  />
                </div>}
                {role != "dealer" && <div className="bg-white p-3 rounded-lg border-2 border-lime-200">
                  <Label className="font-bold text-gray-700 mb-2 block">📐 UOM</Label>
                  <Select
                    value={section.form.area_uom}
                    onValueChange={(val) => handleChange(index, "area_uom", val)}
                    defaultValue="ft"
                  >
                    <SelectTrigger className="border-2 border-lime-300 rounded-lg font-bold">
                      <span>{section.form.area_uom}</span>
                    </SelectTrigger>
                    <SelectContent>
                     { section.selectedProduct?.formulaType !="length" && <SelectItem value="Sq.ft">Sq.ft</SelectItem>} 
                      <SelectItem value="ft">Feet</SelectItem>
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="Mtr.">Mtr.</SelectItem>}
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="Panels">Panels</SelectItem>}
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="No's">No's</SelectItem>}
                      {section.selectedProduct?.formulaType !="length" && <SelectItem value="Pcs.">Pcs.</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>}
              </div>}

              {role != "dealer"  && section && section.selectedProduct  &&  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="bg-white p-3 rounded-lg border-2 border-blue-200">
                  <Label className="font-bold text-gray-700 mb-2 block">💵 Price/Unit (₹)</Label>
                  <Input
                    type="text"
                    step="0.01"
                    value={formatSmartDecimal(section.form.price_per_unit)}
                    onChange={(e) =>
                      handleChange(index, "price_per_unit", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-blue-300 rounded-lg font-bold text-blue-600"
                  />
                </div>
                <div className="bg-white p-3 rounded-lg border-2 border-rose-200">
                  <Label className="font-bold text-gray-700 mb-2 block">💰 Amount (₹) {section.form.custom_name !== undefined && <span className="text-xs text-gray-600">(Final)</span>}</Label>
                  <Input
                    type="text"
                    step="0.01"
                    value={formatSmartDecimal(section.form.price)}
                    onChange={(e) =>
                      handleChange(index, "price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0, true)
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-rose-300 rounded-lg font-bold text-rose-600"
                  />
                </div>
                <div className="bg-white p-3 rounded-lg border-2 border-amber-200">
                  <Label className="font-bold text-gray-700 mb-2 block">🔢 GST %</Label>
                  <Input
                    type="number"
                    value={section.form.gst !== undefined && section.form.gst !== null ? section.form.gst : ""}
                    onChange={(e) =>
                      handleChange(index, "gst", e.target.value === "" ? undefined : parseFloat(e.target.value))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    className="border-2 border-amber-300 rounded-lg font-bold text-amber-600 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]"
                  />
                </div>
              </div>}
            </div>
          ))}
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t-2 border-gray-200 bg-gradient-to-r from-gray-50 to-emerald-50 rounded-b-2xl md:rounded-b-lg shrink-0">
          <Button 
            variant="outline" 
            onClick={() => {
              setSections([
                {
                  selectedProduct: null,
                  form: {
                    ...getDefaultUoms("width_height", false),
                    place: "",
                    gst: 0,
                    price: 0,
                    customProductBasePrice: undefined,
                  },
                },
              ]);
              onClose();
            }}
            className="border-2 border-gray-400 hover:bg-gray-100 font-bold text-gray-700 h-11 rounded-lg"
          >
            ✕ Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold hover:shadow-lg hover:shadow-emerald-500/40 transition-all h-11 rounded-lg px-6"
          >
            ✓ Add
          </Button>
        </div>
      </div>
      </div>

      {/* Product picker modal — same centered popup pattern as customer select */}
      {openPopover !== null && pickerSection && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/40"
          onClick={closeProductPicker}
        >
          <div
            className="bg-white w-full max-w-xl h-[min(90vh,640px)] rounded-2xl shadow-2xl border-2 border-emerald-200 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b-2 border-emerald-100 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-bold text-gray-900">Select Product</h3>
              <button
                type="button"
                onClick={closeProductPicker}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 border-b border-emerald-100 shrink-0 space-y-2">
              <select
                value={pickerCategoryId}
                onChange={(e) => setPickerCategoryId(e.target.value)}
                className="w-full h-11 px-3 border-2 border-emerald-300 rounded-lg bg-white text-sm font-medium text-gray-800"
              >
                <option value="all">All categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <SearchInput
                autoFocus
                placeholder="🔍 Search product name or HSN..."
                value={productSearchInput}
                onValueChange={setProductSearchInput}
                className="border-2 border-emerald-300 rounded-lg h-11 font-medium"
              />
              <p className="text-xs font-semibold text-emerald-800">
                {pickerLoading
                  ? "Searching..."
                  : `${filteredPickerProducts.length}${
                      pickerTotalCount ? ` of ${pickerTotalCount}` : ""
                    } products${productSearchQuery || pickerCategoryId !== "all" ? " matching" : ""}`}
                {normalizeSearchQuery(productSearchInput) &&
                normalizeSearchQuery(productSearchInput) !== productSearchQuery
                  ? " · typing..."
                  : null}
              </p>
            </div>

            <div
              ref={pickerScrollRef}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-2"
              onScroll={handlePickerScroll}
            >
              {pickerLoading && filteredPickerProducts.length === 0 ? (
                <div className="py-12 text-center text-sm text-emerald-700 font-medium">
                  Loading products...
                </div>
              ) : filteredPickerProducts.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500">
                  No matching products
                </div>
              ) : (
                <>
                {filteredPickerProducts.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => handleSelectProduct(openPopover, p)}
                    className={cn(
                      "p-3 rounded-xl cursor-pointer hover:bg-emerald-50 transition-all border-2 flex items-center gap-3",
                      pickerSection.selectedProduct?.id === p.id
                        ? "bg-emerald-50 border-emerald-500 shadow-sm"
                        : "border-transparent hover:border-emerald-200"
                    )}
                  >
                    <AuthMediaImg
                      kind="product"
                      resourceId={p.id}
                      accountId={id}
                      authToken={authToken}
                      inlineSrc={p.productImage}
                      hasImage={Boolean(p.hasImage || p.productImage)}
                      alt={p.name}
                      className="w-12 h-12 rounded-lg border border-emerald-300 object-cover flex-shrink-0 cursor-pointer hover:shadow-lg hover:scale-105 transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewProduct(p);
                      }}
                      fallback={
                        <div className="w-12 h-12 rounded-lg border border-emerald-300 bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-gray-500 text-sm">📦</span>
                        </div>
                      }
                    />
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 truncate">{p.name}</div>
                      {(p as any).categoryName ? (
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {(p as any).categoryName}
                        </div>
                      ) : null}
                      {(p as any).hsnCode ? (
                        <div className="text-xs font-semibold text-emerald-700 mt-0.5">
                          HSN: {(p as any).hsnCode}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
                {pickerLoadingMore ? (
                  <div className="py-3 text-center text-xs font-medium text-emerald-700">
                    Loading more...
                  </div>
                ) : null}
                {!pickerHasMore && filteredPickerProducts.length > 0 ? (
                  <div className="py-2 text-center text-[11px] text-gray-400">End of list</div>
                ) : null}
                </>
              )}
            </div>

            <div className="border-t-2 border-emerald-100 p-4 flex justify-end bg-emerald-50 shrink-0">
              <Button
                type="button"
                size="sm"
                onClick={() => handleAddCustomProduct(openPopover)}
                className="bg-blue-600 text-white hover:bg-blue-700 font-bold"
              >
                + Add Custom Product
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Drawer */}
      {previewColor && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4" onClick={() => setPreviewColor(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              onClick={() => setPreviewColor(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>

            {/* Enlarged Image */}
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-pink-300 shadow-md flex items-center justify-center bg-gray-50">
              <AuthMediaImg
                kind="color"
                resourceId={previewColor.id}
                accountId={id}
                authToken={authToken}
                inlineSrc={getColorImage(previewColor) || previewColor.colorImage}
                hasImage={Boolean(
                  previewColor.hasImage ||
                    previewColor.id ||
                    getColorImage(previewColor) ||
                    previewColor.colorImage
                )}
                alt={previewColor.name}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">No image available</span>
                  </div>
                }
              />
            </div>

            {/* Color Name */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewColor.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Color</p>
            </div>
          </div>
        </div>
      )}

      {/* Product Image Preview Modal */}
      {previewProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center p-4" onClick={() => setPreviewProduct(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              onClick={() => setPreviewProduct(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>

            {/* Enlarged Image */}
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-blue-300 shadow-md flex items-center justify-center bg-gray-50">
              <AuthMediaImg
                kind="product"
                resourceId={previewProduct.id}
                accountId={id}
                authToken={authToken}
                inlineSrc={previewProduct.productImage}
                hasImage={Boolean(
                  previewProduct.hasImage || previewProduct.productImage
                )}
                alt={previewProduct.name}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">No image available</span>
                  </div>
                }
              />
            </div>

            {/* Product Name */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewProduct.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Product</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
